/**
 * OfficialSources - Fontes Oficiais Simplificadas
 * 
 * Baseado no Tripé:
 * ⚖️ JUSTIÇA - Mostra de onde vêm os dados
 * 🔒 SEGURANÇA - Links verificáveis
 * ✨ SIMPLICIDADE - Apenas o essencial
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import AdminPanel from './AdminPanel';
import { useTranslation } from '../../translations';

const OfficialSources = () => {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/market/quotations`);
      if (response.ok) {
        const data = await response.json();
        const quotationsMap = {};
        data.quotations.forEach(q => {
          quotationsMap[q.type] = q;
        });
        setQuotations(quotationsMap);
        setLastUpdate(new Date().toLocaleString('pt-BR'));
      }
    } catch (error) {
      console.error('Erro ao buscar cotações:', error);
    } finally {
      setLoading(false);
    }
  };

  // As 3 fontes essenciais para entender a cadeia
  // ATUALIZADO: GEB-10 APABOR como referência principal
  const essentialSources = [
    {
      id: 'geb10',
      name: t('geb10ApabarMainRef'),
      description: t('geb10ApabarDesc'),
      url: 'https://apabor.org.br',
      quotationKey: 'geb10_apabor',
      icon: '🏭',
      color: 'border-blue-500 bg-blue-50',
      step: 1,
    },
    {
      id: 'coagulo',
      name: t('coaguloIeaProducerPrice'),
      description: t('coaguloIeaDesc'),
      url: 'https://www.noticiasagricolas.com.br/cotacoes/latex',
      quotationKey: 'coagulo',
      icon: '🌿',
      color: 'border-green-500 bg-green-50',
      step: 2,
    },
    {
      id: 'custo',
      name: t('productionCostConab'),
      description: t('productionCostConabDesc'),
      url: 'https://www.conab.gov.br',
      quotationKey: 'custo_producao_cot',
      icon: '📦',
      color: 'border-amber-500 bg-amber-50',
      step: 3,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Painel Admin */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)} 
          onUpdate={fetchQuotations}
        />
      )}

      {/* Cotações Atuais */}
      <Card className="border-2 border-green-500">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-green-800 flex items-center gap-2">
              <span className="text-2xl">💰</span>
              {t('heveaTalentQuotations')}
            </CardTitle>
            <button
              onClick={() => setShowAdmin(true)}
              className="text-xs px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
            >
              ⚙️ {t('update')}
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-4">
              <span className="animate-spin text-2xl">⏳</span>
            </div>
          ) : (
            <>
              {/* Cards principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {quotations.preco_importacao && (
                  <div className="bg-blue-50 rounded-xl p-3 border-2 border-blue-300">
                    <div className="text-xs text-blue-600 font-medium">📊 {t('importPrice')}</div>
                    <div className="text-xl font-bold text-blue-800">
                      R$ {quotations.preco_importacao.value?.toFixed(2).replace('.', ',')}
                    </div>
                    <div className="text-xs text-gray-500">{quotations.preco_importacao.reference_date}</div>
                  </div>
                )}
                
                {quotations.geb10_apabor && (
                  <div className="bg-green-50 rounded-xl p-3 border-2 border-green-300">
                    <div className="text-xs text-green-600 font-medium">🏭 {t('geb10Label')}</div>
                    <div className="text-xl font-bold text-green-800">
                      R$ {quotations.geb10_apabor.value?.toFixed(2).replace('.', ',')}
                    </div>
                    <div className="text-xs text-gray-500">{quotations.geb10_apabor.reference_date}</div>
                  </div>
                )}
                
                {quotations.coagulo && (
                  <div className="bg-amber-50 rounded-xl p-3 border-2 border-amber-300">
                    <div className="text-xs text-amber-600 font-medium">🌳 {t('coaguloLabel')}</div>
                    <div className="text-xl font-bold text-amber-800">
                      R$ {quotations.coagulo.value?.toFixed(2).replace('.', ',')}
                    </div>
                    <div className="text-xs text-gray-500">{quotations.coagulo.reference_date}</div>
                  </div>
                )}
                
                {quotations.internacional && (
                  <div className="bg-purple-50 rounded-xl p-3 border-2 border-purple-300">
                    <div className="text-xs text-purple-600 font-medium">🌍 {t('internationalLabel')}</div>
                    <div className="text-xl font-bold text-purple-800">
                      US$ {quotations.internacional.value?.toFixed(4)}
                    </div>
                    <div className="text-xs text-gray-500">{quotations.internacional.reference_date}</div>
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <div className="text-xs text-gray-500 text-center border-t pt-2">
                📅 {t('lastVerification')}: {lastUpdate}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cadeia de Formação de Preço - Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center gap-2">
            <span className="text-xl">🔗</span>
            {t('howPriceIsFormed')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 text-sm mb-4">
            {t('understandWhereDataComesFrom')}
          </p>

          {/* Fluxo visual da cadeia */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            {essentialSources.map((source, index) => (
              <React.Fragment key={source.id}>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex-1 p-4 rounded-xl border-2 ${source.color} hover:shadow-lg transition-shadow text-center`}
                >
                  <div className="text-3xl mb-2">{source.icon}</div>
                  <div className="font-bold text-gray-800 text-sm">{source.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{source.description}</div>
                  
                  {/* Valor atual */}
                  {quotations[source.quotationKey] && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-lg font-bold text-gray-800">
                        R$ {quotations[source.quotationKey].value?.toFixed(2).replace('.', ',')}
                      </div>
                      <div className="text-xs text-blue-600 mt-1">🔗 {t('verifySource')}</div>
                    </div>
                  )}
                </a>
                
                {/* Seta entre os cards */}
                {index < essentialSources.length - 1 && (
                  <div className="text-2xl text-gray-400 hidden md:block">→</div>
                )}
                {index < essentialSources.length - 1 && (
                  <div className="text-2xl text-gray-400 md:hidden">↓</div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Fórmula simplificada */}
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <p className="text-sm text-gray-600 mb-2">📐 {t('priceFormulaProducer')}:</p>
            <div className="text-lg font-mono font-bold text-gray-800">
              {t('importPrice')} × DRC × {t('participationRate')} = {t('yourPrice')}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t('priceFormulaExample')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Outras fontes do estudo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-700">📚 Demais fontes usadas no estudo e na plataforma</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-gray-600 space-y-1.5" data-testid="study-sources-list">
            <li>• <strong>CNA</strong> — índice de preço de referência de importação (com IEA, desde 2020) · cnabrasil.org.br</li>
            <li>• <strong>SGX / Trading Economics</strong> — cotação internacional TSR-20 (diária) · tradingeconomics.com</li>
            <li>• <strong>Banco Central (PTAX)</strong> — câmbio oficial diário usado nos cálculos · bcb.gov.br</li>
            <li>• <strong>World Bank "Pink Sheet"</strong> — médias mensais do TSR-20 usadas no backtest do modelo</li>
            <li>• <strong>Painel Aberto</strong> — série mensal pública do índice IEA/CNA · painelaberto.com.br</li>
            <li>• <strong>CONAB / MAPA</strong> — preço mínimo PGPM e leilões PEP/PEPRO</li>
            <li>• <strong>COOESTE / Centro Oeste Borrachas</strong> — fechamento bimestral GEB-10 SP</li>
            <li>• <strong>FAESP/SENAR</strong> — custos de produção da borracha natural em SP</li>
          </ul>
          <a
            href="/estudo-preco-coagulo-2012-2026.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="study-pdf-link"
            className="inline-block mt-3 text-xs font-semibold text-green-700 hover:underline"
          >
            📄 Baixar o estudo completo em PDF (2012–2026) →
          </a>
        </CardContent>
      </Card>

      {/* Explicação do Tripé */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <span className="text-2xl">⚖️</span>
              <h4 className="font-bold text-green-800 mt-1">{t('fairness').toUpperCase()}</h4>
              <p className="text-xs text-green-700">{t('dataVerifiableOfficial')}</p>
            </div>
            <div>
              <span className="text-2xl">🔒</span>
              <h4 className="font-bold text-green-800 mt-1">{t('security').toUpperCase()}</h4>
              <p className="text-xs text-green-700">{t('youCheckBeforeUsing')}</p>
            </div>
            <div>
              <span className="text-2xl">✨</span>
              <h4 className="font-bold text-green-800 mt-1">{t('simplicity').toUpperCase()}</h4>
              <p className="text-xs text-green-700">{t('onlyWhatMatters')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OfficialSources;
